import { IconLayer } from '@deck.gl/layers';
import type { LayerState } from '@vitals/shared';
import { routeColor } from '@vitals/shared';
import { createCometMotion } from './cometMotion.js';
import { routeBulletPresentation } from '../routeBulletPresentation.js';

// Re-export distanceAtTime so existing test imports from bullets.js remain valid.
export { distanceAtTime } from './cometMotion.js';

/** Interpolate a train's position along its path at absolute time `t` (epoch s).
 * Path coords and timestamps are aligned 1:1. Clamps before/after the window. */
export function positionAtTime(
  path: [number, number][],
  timestamps: number[],
  t: number,
): [number, number] {
  const n = Math.min(path.length, timestamps.length);
  if (n === 0) return [0, 0];
  if (t <= timestamps[0]!) return path[0]!;
  if (t >= timestamps[n - 1]!) return path[n - 1]!;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (timestamps[mid]! <= t) lo = mid;
    else hi = mid;
  }
  const t0 = timestamps[lo]!;
  const t1 = timestamps[hi]!;
  const f = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
  const a = path[lo]!;
  const b = path[hi]!;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

/** Yellow lines get black text on the bullet; everything else white. */
export function bulletTextColor(routeId: string): '#000' | '#fff' {
  return ['N', 'Q', 'R', 'W'].includes(routeId.toUpperCase()) ? '#000' : '#fff';
}

/** The short designation shown inside the bullet (handles shuttles/express). */
export function bulletLabel(routeId: string): string {
  return routeBulletPresentation(routeId).label;
}

// All route bullets we pre-render into the atlas. Anything else falls back to grey.
const ROUTES = [
  '1', '2', '3', '4', '5', '6', '7', 'A', 'C', 'E', 'B', 'D', 'F', 'M', 'G', 'J',
  'Z', 'L', 'N', 'Q', 'R', 'W', 'S', 'SI', 'SIR', '5X', '6X', '7X', 'FX', 'H', 'GS', 'FS',
];
const CELL = 64; // px per bullet in the atlas
const FALLBACK = 'FALLBACK';

export interface BulletAtlas {
  /** PNG data URL of the sprite sheet (deck.gl IconLayer iconAtlas). */
  url: string;
  mapping: Record<string, { x: number; y: number; width: number; height: number; mask: boolean }>;
}

let cachedAtlas: BulletAtlas | null = null;

/** Build (once) a sprite atlas of MTA bullets in their route shape and color. */
export function buildBulletAtlas(): BulletAtlas {
  if (cachedAtlas) return cachedAtlas;
  const ids = [...new Set(ROUTES.map((r) => r.toUpperCase()))];
  const entries = [...ids, FALLBACK];
  const cols = Math.ceil(Math.sqrt(entries.length));
  const rows = Math.ceil(entries.length / cols);
  const atlas = document.createElement('canvas');
  atlas.width = cols * CELL;
  atlas.height = rows * CELL;
  const ctx = atlas.getContext('2d')!;
  const mapping: BulletAtlas['mapping'] = {};

  entries.forEach((id, i) => {
    const cx = (i % cols) * CELL;
    const cy = Math.floor(i / cols) * CELL;
    const r = CELL / 2 - 4;
    const [cr, cg, cb] = id === FALLBACK ? [136, 136, 136] : routeColor(id);
    const presentation = routeBulletPresentation(id);
    ctx.beginPath();
    if (presentation.shape === 'diamond') {
      ctx.moveTo(cx + CELL / 2, cy + CELL / 2 - r);
      ctx.lineTo(cx + CELL / 2 + r, cy + CELL / 2);
      ctx.lineTo(cx + CELL / 2, cy + CELL / 2 + r);
      ctx.lineTo(cx + CELL / 2 - r, cy + CELL / 2);
      ctx.closePath();
    } else {
      ctx.arc(cx + CELL / 2, cy + CELL / 2, r, 0, Math.PI * 2);
    }
    ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
    ctx.fill();
    if (id !== FALLBACK) {
      const label = presentation.label;
      ctx.fillStyle = bulletTextColor(id);
      const fontSize = label.length >= 3 ? 20 : label.length === 2 ? 30 : 38;
      ctx.font = `700 ${fontSize}px Helvetica, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const labelOffsetY = presentation.shape === 'diamond' ? 2 : 1;
      ctx.fillText(label, cx + CELL / 2, cy + CELL / 2 + labelOffsetY);
    }
    mapping[id] = { x: cx, y: cy, width: CELL, height: CELL, mask: false };
  });

  cachedAtlas = { url: atlas.toDataURL(), mapping };
  return cachedAtlas;
}

interface BulletDatum {
  position: [number, number];
  icon: string;
  label: string; // line designation, for the hover tooltip
  size: number;
  destination?: string;
  nextStop?: string;
  nextStopAt?: number;
  nowMs: number;
}

const BULLET_SIZE = 18; // px
const EXPRESS_BULLET_SIZE = BULLET_SIZE * 1.2;
// Each MTA poll (15s) revises predictions, so ~20% of trains' recomputed
// positions hop 200-600m. We close that gap at a CONSTANT capped speed rather
// than easing (exponential easing spikes velocity right after the poll then
// decays — a visible speed-up-then-slow-down). At a gentle cap, a typical
// correction spreads across ~the poll interval, so motion stays uniform.
//
// Critically, the catch-up runs in ALONG-PATH distance space, not straight-line
// lng/lat: stepping the displayed distance and mapping it back to a point ON the
// polyline keeps the bullet glued to the track curve even mid-correction. (A
// straight-line follow cuts chords across curves — the "slightly off the path"
// drift.) Trains don't reverse, so backward corrections hold.

const subwayMotion = createCometMotion();

export function buildSubwayBulletLayer(state: LayerState, nowSec: number): IconLayer<BulletDatum> {
  const { url, mapping } = buildBulletAtlas();
  const data: BulletDatum[] = subwayMotion.step(state, nowSec).map((s) => {
    const presentation = routeBulletPresentation(s.props.routeId);
    return {
      position: s.position,
      icon: mapping[presentation.id] ? presentation.id : FALLBACK,
      label: presentation.descriptor,
      size: presentation.shape === 'diamond' ? EXPRESS_BULLET_SIZE : BULLET_SIZE,
      destination: s.props.destination,
      nextStop: s.props.nextStop,
      nextStopAt: s.props.nextStopAt,
      nowMs: nowSec * 1000,
    };
  });
  return new IconLayer<BulletDatum>({
    id: 'subway',
    data, // recomputed each frame, so positions drift continuously at real time
    iconAtlas: url,
    iconMapping: mapping,
    getIcon: (d) => d.icon,
    getPosition: (d) => d.position,
    getSize: (d) => d.size,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
    // No collision declutter: overlapping bullets simply draw on top of each
    // other. Decluttering hid bullets in dense areas (and caused flicker as
    // positions drifted frame-to-frame), so we let trains overlap instead.
  });
}
